/**
 * [BL] Add-on Categories — thumbnails that open the quick-add modal
 * (blocks/addon-categories.liquid, snippets/bl-addon-tile.liquid).
 *
 * Ported from bylumine_hydrogen (AdditionalProductCategorySection). Each tile sits inside a
 * <quick-add-component data-product-url="…?variant=…">, so the theme's own quick-add flow
 * fetches that product page and opens the modal with the matched variant preselected.
 * The modal's add-to-cart then works exactly like quick add on a collection page.
 */
import { Component } from '@theme/component';

class AddonCategoriesComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    // Hold mode: outline the tiles that are in the shopper's set and show how many.
    this.addEventListener('bl-tray:change', this.#markHeld);
    queueMicrotask(this.#markHeld);
    customElements.whenDefined('bl-addon-tray').then(this.#markHeld);

    // Tabs layout: the raised indicator that slides to the selected tab.
    const tablist = /** @type {HTMLElement | null} */ (this.querySelector('.bl-addons__tabs'));
    if (tablist) {
      tablist.addEventListener('scroll', this.#updateScrollCues, { passive: true });

      const indicator = document.createElement('span');
      indicator.className = 'bl-addons__tab-indicator';
      indicator.setAttribute('aria-hidden', 'true');
      tablist.prepend(indicator);
      this.#positionIndicator(false);
      tablist.setAttribute('data-indicator', '');
      this.resizeObserver = new ResizeObserver(() => {
        this.#positionIndicator(false);
        this.#updateScrollCues();
      });
      this.resizeObserver.observe(tablist);
      // The row's own box can stay the same width while its tabs grow (web fonts, the stylesheet
      // arriving): watch the tabs too, so the arrow and fade still appear when they overflow.
      for (const tab of tablist.querySelectorAll('.bl-addons__tab')) this.resizeObserver.observe(tab);
      document.fonts?.ready.then(() => {
        this.#positionIndicator(false);
        this.#updateScrollCues();
      });
    }
  }

  /** Fade + arrow on whichever side has more tabs off-screen. */
  #updateScrollCues = () => {
    const tablist = /** @type {HTMLElement | null} */ (this.querySelector('.bl-addons__tabs'));
    const wrap = tablist?.parentElement;
    if (!tablist || !wrap) return;
    const maxScroll = tablist.scrollWidth - tablist.clientWidth;
    const moreStart = tablist.scrollLeft > 4;
    const moreEnd = maxScroll - tablist.scrollLeft > 4;
    wrap.toggleAttribute('data-more-start', moreStart);
    wrap.toggleAttribute('data-more-end', moreEnd);
    wrap.querySelector('.bl-addons__tabs-arrow--prev')?.toggleAttribute('hidden', !moreStart);
    wrap.querySelector('.bl-addons__tabs-arrow--next')?.toggleAttribute('hidden', !moreEnd);
  };

  /** Arrow buttons: scroll the tab row most of a screenful. @param {{ direction: string }} data */
  scrollTabs = (data) => {
    const tablist = /** @type {HTMLElement | null} */ (this.querySelector('.bl-addons__tabs'));
    if (!tablist) return;
    const direction = Number(data?.direction) < 0 ? -1 : 1;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    tablist.scrollBy({ left: direction * tablist.clientWidth * 0.7, behavior: reducedMotion ? 'auto' : 'smooth' });
    // Belt and braces for browsers that coalesce scroll events during a smooth scroll.
    setTimeout(this.#updateScrollCues, 450);
  };

  /** @param {boolean} animate */
  #positionIndicator(animate) {
    const tablist = /** @type {HTMLElement | null} */ (this.querySelector('.bl-addons__tabs'));
    const tab = /** @type {HTMLElement | null} */ (this.querySelector('.bl-addons__tab[aria-selected="true"]'));
    const indicator = /** @type {HTMLElement | null} */ (tablist?.querySelector('.bl-addons__tab-indicator'));
    if (!tablist || !tab || !indicator) return;
    if (!animate) indicator.style.transition = 'none';
    tablist.style.setProperty('--bl-tab-x', `${tab.offsetLeft}px`);
    tablist.style.setProperty('--bl-tab-width', `${tab.offsetWidth}px`);
    if (!animate) {
      void indicator.offsetWidth;
      indicator.style.transition = '';
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('bl-tray:change', this.#markHeld);
    this.resizeObserver?.disconnect();
  }

  #markHeld = () => {
    const tray = /** @type {any} */ (this.querySelector('bl-addon-tray'));
    /** @type {Map<string, number>} */
    const held = new Map();
    for (const item of tray?.items || []) {
      held.set(String(item.variantId), (held.get(String(item.variantId)) || 0) + (item.quantity || 1));
    }
    for (const tile of this.querySelectorAll('.bl-addons__tile')) {
      const url = /** @type {HTMLElement} */ (tile).dataset.productUrl || '';
      const variantId = new URL(url, window.location.origin).searchParams.get('variant') || '';
      const count = held.get(variantId) || 0;
      tile.toggleAttribute('data-in-set', count > 0);
      let badge = tile.querySelector('.bl-addons__count');
      if (count > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'bl-addons__count';
          // "1 added"; on tiles too narrow for the word, "1" and a tick (container query in the block's CSS).
          badge.innerHTML =
            '<span class="bl-addons__count-number"></span><span class="bl-addons__count-label"></span>' +
            '<svg class="bl-addons__count-tick" viewBox="0 0 12 12" aria-hidden="true" focusable="false">' +
            '<path d="M2.5 6.4l2.3 2.3 4.7-5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
          tile.append(badge);
        }
        const added = String(/** @type {any} */ (window).Theme?.translations?.added || 'Added').toLowerCase();
        /** @type {HTMLElement} */ (badge.querySelector('.bl-addons__count-number')).textContent = String(count);
        /** @type {HTMLElement} */ (badge.querySelector('.bl-addons__count-label')).textContent = added;
        badge.setAttribute('aria-label', `${count} ${added}`);
      } else {
        badge?.remove();
      }
    }
  };

  /** Tabs layout: show one category's grid. @param {Event} event */
  selectTab = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('.bl-addons__tab') : null;
    if (tab) this.#activateTab(/** @type {HTMLElement} */ (tab));
  };

  /** Arrow keys move between tabs (roving tabindex). @param {KeyboardEvent} event */
  tabKeydown = (event) => {
    const tabs = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.bl-addons__tab')));
    const current = tabs.indexOf(/** @type {HTMLElement} */ (document.activeElement));
    if (current < 0) return;
    let next = current;
    if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;
    event.preventDefault();
    const target = tabs[next];
    if (!target) return;
    this.#activateTab(target);
    target.focus();
  };

  /** @param {HTMLElement} tab */
  #activateTab(tab) {
    for (const other of this.querySelectorAll('.bl-addons__tab')) {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      if (selected) other.removeAttribute('tabindex');
      else other.setAttribute('tabindex', '-1');
    }
    const panelId = tab.getAttribute('aria-controls');
    for (const panel of this.querySelectorAll('[data-bl-addons-panel]')) {
      panel.toggleAttribute('hidden', panel.id !== panelId);
    }
    tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    this.#positionIndicator(true);
  }

  /** Tabs layout: reveal the rest of a category. @param {Event} event */
  expandCategory = (event) => {
    const panel = event.target instanceof Element ? event.target.closest('.bl-addons__category') : null;
    panel?.setAttribute('data-expanded', '');
  };

  /** Reveal the categories beyond custom.additionalproductcategorieshighlighted. */
  showAll = () => {
    for (const category of this.querySelectorAll('[data-bl-addons-extra]')) {
      category.removeAttribute('hidden');
    }
    this.refs.showMoreButton?.remove();
  };

  /** @param {Event} event */
  openQuickAdd = async (event) => {
    const tile = /** @type {HTMLButtonElement | null} */ (
      event.target instanceof Element ? event.target.closest('.bl-addons__tile') : null
    );
    if (!tile || tile.disabled || tile.getAttribute('aria-busy') === 'true') return;

    const productUrl = tile.dataset.productUrl;
    const quickAdd = /** @type {import('./quick-add').QuickAddComponent | null} */ (
      tile.closest('quick-add-component')
    );

    // The modal is only rendered when quick add is enabled in theme settings — otherwise
    // (or if quick-add.js hasn't upgraded the element yet) fall back to the product page.
    if (!document.getElementById('quick-add-dialog') || typeof quickAdd?.handleClick !== 'function') {
      if (productUrl) window.location.href = productUrl;
      return;
    }

    tile.setAttribute('aria-busy', 'true');
    try {
      await quickAdd.handleClick(event);
    } catch (error) {
      console.error(error);
      if (productUrl) window.location.href = productUrl;
    } finally {
      tile.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('addon-categories-component')) {
  customElements.define('addon-categories-component', AddonCategoriesComponent);
}
